import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, ClipboardList } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type {
  OffboardingProcessView,
  OffboardingProcessTaskView,
  ExitReasonView,
} from '../../../models/employee';
import { ProcessTaskStatus } from '../../../models/employee';
import {
  getOffboardingProcess,
  updateOffboarding,
  addOffboardingTask,
  removeOffboardingTask,
  updateOffboardingTaskStatus,
} from '../../../api/Employee/offboardingProcesses';
import { getExitReasons } from '../../../api/Employee/lifecycleSettings';
import { appDate } from '../../../utils/appDateFormat';
import { DateField } from '../../../components/dateFields';
import { apiDateOnlyParam } from '@/utils/apiDate';

interface EditOffboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  process: OffboardingProcessView | null;
}

const EditOffboardingModal: React.FC<EditOffboardingModalProps> = ({ isOpen, onClose, onSubmit, process }) => {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState<OffboardingProcessTaskView[]>([]);
  const [exitReasons, setExitReasons] = useState<ExitReasonView[]>([]);
  const [newItemName, setNewItemName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    lastWorkingDay: '',
    exitReasonId: '',
    managerId: '',
  });

  useEffect(() => {
    if (isOpen && process) {
      // Load fresh process data
      getOffboardingProcess(process.id).then(result => {
        if (!result.errors || result.errors.length === 0) {
          const p = result.payload;
          if (p) {
            setTasks(p.tasks || []);
            setFormData({
              lastWorkingDay: apiDateOnlyParam(p.lastWorkingDay),
              exitReasonId: String(p.exitReasonId),
              managerId: p.managerId,
            });
          }
        }
      });
      // Load exit reasons
      getExitReasons().then(result => {
        if (!result.errors || result.errors.length === 0) {
          setExitReasons((result.payload || []).filter(r => r.isActive));
        }
      });
      setError(null);
    }
  }, [isOpen, process]);

  const handleAddItem = async () => {
    if (!newItemName.trim() || !process) return;

    const result = await addOffboardingTask(process.id, {
      name: newItemName,
      mandatory: true,
      isBlocking: false,
      dueDate: formData.lastWorkingDay || undefined,
      sortOrder: tasks.length,
    });

    if (!result.errors || result.errors.length === 0) {
      if (result.payload) {
        setTasks(prev => [...prev, result.payload!]);
      }
      setNewItemName('');
    }
  };

  const handleDeleteItem = async (taskId: number) => {
    const result = await removeOffboardingTask(taskId);
    if (!result.errors || result.errors.length === 0) {
      setTasks(prev => prev.filter(t => t.id !== taskId));
    }
  };

  const handleUpdateItemStatus = async (taskId: number, newStatus: ProcessTaskStatus) => {
    const result = await updateOffboardingTaskStatus(taskId, { status: newStatus });
    if (!result.errors || result.errors.length === 0) {
      if (result.payload) {
        setTasks(prev => prev.map(t => t.id === taskId ? result.payload! : t));
      }
    }
  };

  const getStatusColor = (status: ProcessTaskStatus): string => {
    switch (status) {
      case ProcessTaskStatus.Completed: return 'bg-green-100 text-green-800';
      case ProcessTaskStatus.Cancelled: return 'bg-red-100 text-red-800';
      case ProcessTaskStatus.InProgress: return 'bg-blue-100 text-blue-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!process) return;
    setSaving(true);
    setError(null);
    try {
      const result = await updateOffboarding(process.id, {
        userId: process.userId,
        templateId: process.templateId || undefined,
        lastWorkingDay: formData.lastWorkingDay,
        exitReasonId: parseInt(formData.exitReasonId),
        managerId: formData.managerId,
      });
      if (result.errors && result.errors.length > 0) {
        setError(result.errors[0]?.message || 'Failed to save changes');
      } else {
        onSubmit();
      }
    } catch {
      setError('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('Edit Offboarding Process')}</h3>
            <p className="text-sm text-gray-600 mt-1">{t('Update offboarding details and checklist for')} {process?.employeeName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          {/* Employee Details (Read-only) */}
          <div className="bg-muted border border-border rounded-lg p-4">
            <div>
              <span className="text-xs text-gray-500">{t('Employee Name')}</span>
              <div className="text-sm font-medium text-gray-900">{process?.employeeName}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Last Working Day */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('Last Working Day')} <span className="text-red-500">*</span>
              </label>
              <DateField
                required
                value={formData.lastWorkingDay}
                onChange={(v) => setFormData(prev => ({ ...prev, lastWorkingDay: v }))}
              />
            </div>

            {/* Exit Reason */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('Exit Reason')} <span className="text-red-500">*</span>
              </label>
              <select
                required
                value={formData.exitReasonId}
                onChange={(e) => setFormData(prev => ({ ...prev, exitReasonId: e.target.value }))}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">{t('Select exit reason...')}</option>
                {exitReasons.map(reason => (
                  <option key={reason.id} value={reason.id}>{t(reason.name)}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Offboarding Checklist Section */}
          <div className="border-t border-border pt-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-md font-semibold text-gray-900">{t('Exit Checklist')}</h4>
              <span className="text-sm text-gray-600">{tasks.length} {t('items')}</span>
            </div>

            {/* Add New Item */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-end space-x-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('Item Name')}</label>
                  <input
                    type="text"
                    value={newItemName}
                    onChange={(e) => setNewItemName(e.target.value)}
                    placeholder={t('Enter checklist item name...')}
                    className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleAddItem}
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors text-sm font-medium flex items-center"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  {t('Add Item')}
                </button>
              </div>
            </div>

            {/* Checklist Items */}
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {tasks.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <ClipboardList className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">{t('No checklist items yet. Add your first item above.')}</p>
                </div>
              ) : (
                tasks.map((item) => (
                  <div key={item.id} className="bg-card border border-border rounded-lg p-3 hover:border-border transition-colors">
                    <div className="flex items-start space-x-3">
                      {/* Status Dropdown */}
                      <select
                        value={item.status}
                        onChange={(e) => handleUpdateItemStatus(item.id, parseInt(e.target.value) as ProcessTaskStatus)}
                        className={`px-2 py-1 text-xs font-medium rounded border-0 focus:outline-none focus:ring-2 focus:ring-primary ${getStatusColor(item.status)}`}
                      >
                        <option value={ProcessTaskStatus.Pending}>{t('Pending')}</option>
                        <option value={ProcessTaskStatus.InProgress}>{t('In Progress')}</option>
                        <option value={ProcessTaskStatus.Completed}>{t('Completed')}</option>
                        <option value={ProcessTaskStatus.Cancelled}>{t('Cancelled')}</option>
                      </select>

                      {/* Item Content */}
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          {item.name}
                        </div>
                        <div className="flex items-center space-x-3 mt-1">
                          <span className="text-xs text-gray-600">{item.typeName || '-'}</span>
                          <span className="text-xs text-gray-500">{t('Due')}: {item.dueDate ? appDate(item.dueDate) : '-'}</span>
                          {item.mandatory && (
                            <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded">{t('Required')}</span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center space-x-1">
                        <button
                          type="button"
                          onClick={() => handleDeleteItem(item.id)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          title={t('Delete item')}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              {t('Cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {saving ? t('Saving...') : t('Save Changes')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditOffboardingModal;
